<template>
    <div>
        <div v-auto-animate>
            <CalendarIntegrations
                v-if="
                    currentStep === 'calendar-integrations' &&
                    (!googleIntegration || googleIntegration.errors)
                "
                :google-integration="googleIntegration"
                :i-cloud-integration="iCloudIntegration"
            />
            <ChooseIntegrationCalendar
                v-if="currentStep === 'choose-integration-calendar'"
                :integration="googleIntegration ?? iCloudIntegration"
                :event-type="eventType"
            />
            <Brand v-if="currentStep === 'brand'" :appearance="appearance" />
            <Availability
                v-if="currentStep === 'availability'"
                :availability="availability"
            />
        </div>

        <div
            class="flex"
            :class="[isSkipable ? 'justify-end gap-2' : 'justify-between']"
        >
            <Button v-if="!isSkipable" variant="outline" @click="previousStep"
                >{{ $t("onboarding.back") }}
            </Button>
            <Button v-if="!isSkipable" @click="nextStep">{{
                $t("onboarding.continue")
            }}</Button>

            <Button v-if="isSkipable" variant="outline" @click="nextStep">
                {{ $t("onboarding.skip") }}</Button
            >
        </div>
    </div>
</template>

<script setup lang="ts">
import CalendarIntegrations from "./CalendarIntegrations.vue";
import Brand from "./Brand.vue";
import Availability from "./Availability.vue";
import { Button } from "@/components/ui/button";
import ChooseIntegrationCalendar from "./ChooseIntegrationCalendar.vue";
import { IAppearance } from "@/types/IAppearance";
import { computed, ref } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";

const props = defineProps<{
    availability: any;
    googleIntegration?: any;
    appearance: IAppearance;
    eventType: any;
    iCloudIntegration?: any;
}>();

const validGoogleIntegration = computed(() => {
    if (!props.googleIntegration) {
        return true;
    }

    return props.googleIntegration.isValid;
});

const validICloudCalendarIntegration = computed(() => {
    if (!props.iCloudIntegration) {
        return true;
    }

    return props.iCloudIntegration.isValid;
});

const steps =
    (props.googleIntegration && validGoogleIntegration.value) ||
    (props.iCloudIntegration && props.iCloudIntegration.isValid)
        ? (["choose-integration-calendar", "brand", "availability"] as const)
        : ([
              "calendar-integrations",
              "choose-integration-calendar",
              "brand",
              "availability",
          ] as const);

const isSkipable = computed(() => {
    return currentStep.value === "calendar-integrations";
});

type Step = (typeof steps)[number];

const currentStep = ref<Step>(
    (props.googleIntegration && validGoogleIntegration.value) ||
        (props.iCloudIntegration && props.iCloudIntegration.isValid)
        ? "choose-integration-calendar"
        : "calendar-integrations",
);

const finish = () => {
    router.post(
        route("app.onboard.finish"),
        {},
        {
            preserveScroll: true,
            preserveState: true,
        },
    );
};

const nextStep = () => {
    //@ts-expect-error
    const currentIndex = steps.indexOf(currentStep.value);
    if (currentIndex === steps.length - 1) {
        finish();
        return;
    }

    if (
        currentStep.value === "calendar-integrations" &&
        (!props.googleIntegration || !validGoogleIntegration.value) &&
        !props.iCloudIntegration
    ) {
        currentStep.value = "brand";
        return;
    }

    currentStep.value = steps[currentIndex + 1];
};

const previousStep = () => {
    //@ts-expect-error
    const currentIndex = steps.indexOf(currentStep.value);
    if (currentIndex === 0) {
        return;
    }

    if (
        currentStep.value === "brand" &&
        (!props.googleIntegration || !validGoogleIntegration.value) &&
        !props.iCloudIntegration
    ) {
        currentStep.value = "calendar-integrations";
        return;
    }

    currentStep.value = steps[currentIndex - 1];
};
</script>
